// Formatting + label maps for the link-token registry UI.
// Kept in one place so the table, filters, drawer and dialogs stay consistent.

export const STAGE_LABELS = {
  REGISTRATION: 'Registration',
  RECTIFICATION: 'Rectification',
  DOC_REPLACEMENT: 'Doc Replacement',
  ACKNOWLEDGEMENT: 'Acknowledgement',
  REGULARIZATION: 'Regularization',
};

// Reuse the existing Badge className style (bg-*/text-*/border-*), matching the
// STATUS_COLORS pattern already used across this page.
export const STAGE_BADGE_CLASS = {
  REGISTRATION: 'bg-blue-100 text-blue-800 border-blue-200',
  RECTIFICATION: 'bg-orange-100 text-orange-800 border-orange-200',
  DOC_REPLACEMENT: 'bg-purple-100 text-purple-800 border-purple-200',
  ACKNOWLEDGEMENT: 'bg-teal-100 text-teal-800 border-teal-200',
  REGULARIZATION: 'bg-slate-100 text-slate-800 border-slate-200',
};

export const STATUS_LABELS = {
  ISSUED: 'Issued',
  VIEWED: 'Viewed',
  CONSUMED: 'Consumed',
  EXPIRED: 'Expired',
  SUPERSEDED: 'Superseded',
  REVOKED: 'Revoked',
};

export const STATUS_BADGE_CLASS = {
  ISSUED: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  VIEWED: 'bg-blue-100 text-blue-800 border-blue-200',
  CONSUMED: 'bg-green-100 text-green-800 border-green-200',
  EXPIRED: 'bg-red-100 text-red-800 border-red-200',
  SUPERSEDED: 'bg-slate-100 text-slate-600 border-slate-200',
  REVOKED: 'bg-slate-200 text-slate-700 border-slate-300',
};

// (STAGE_OPTIONS removed — stage is now selected via the StageTabs tab bar,
// not a multi-select. STATUS_OPTIONS remains for the status filter.)
export const STATUS_OPTIONS = Object.keys(STATUS_LABELS).map((value) => ({
  value,
  label: STATUS_LABELS[value],
}));

const ACTIVE_STATUSES = ['ISSUED', 'VIEWED'];
const TERMINAL_STATUSES = ['CONSUMED', 'SUPERSEDED', 'REVOKED'];

export const isActiveStatus = (status) => ACTIVE_STATUSES.includes(status);

/**
 * The status the row actually has right now.
 *
 * The stored `status` column only flips to EXPIRED when the hourly link-reminder
 * sweep runs, and that sweep is skipped entirely when Redis is unavailable — so
 * a past-due row can sit at ISSUED indefinitely and the table would keep showing
 * a yellow "Issued" pill for a link that stopped working days ago.
 *
 * The backend now sends `effective_status`; this falls back to deriving it
 * locally so the pill is right even against an older API response.
 */
export const getEffectiveStatus = (row) => {
  if (!row) return undefined;
  if (row.effective_status) return row.effective_status;
  if (isActiveStatus(row.status) && new Date(row.expires_at) <= new Date()) {
    return 'EXPIRED';
  }
  return row.status;
};
export const isTerminalStatus = (status) => TERMINAL_STATUSES.includes(status);

/**
 * A link is "live" (copyable / openable) only when it is active AND not past
 * expiry. Consumed/expired/superseded/revoked links have no working URL.
 */
export const hasLiveLink = (row) => {
  if (!isActiveStatus(row.status)) return false;
  return new Date(row.expires_at) > new Date();
};

/**
 * Human-friendly expiry text with live-ish countdown, mirroring the existing
 * public-registration countdown style ("3 days left", "expired 2 days ago").
 */
export const formatExpiryCountdown = (expiresAt) => {
  const now = Date.now();
  const exp = new Date(expiresAt).getTime();
  const diffMs = exp - now;
  const dayMs = 24 * 60 * 60 * 1000;

  if (diffMs <= 0) {
    const days = Math.floor(-diffMs / dayMs);
    if (days <= 0) return { text: 'expired today', expired: true };
    if (days === 1) return { text: 'expired 1 day ago', expired: true };
    return { text: `expired ${days} days ago`, expired: true };
  }

  const days = Math.floor(diffMs / dayMs);
  const hours = Math.floor((diffMs % dayMs) / (60 * 60 * 1000));
  if (days >= 1) {
    return { text: `${days} day${days === 1 ? '' : 's'} left`, expired: false, soon: days < 2 };
  }
  if (hours >= 1) {
    return { text: `${hours} hour${hours === 1 ? '' : 's'} left`, expired: false, soon: true };
  }
  const minutes = Math.max(1, Math.floor(diffMs / (60 * 1000)));
  return { text: `${minutes} min left`, expired: false, soon: true };
};

export const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

/**
 * Copy text to clipboard with the same fallback the existing InviteSummaryModal
 * uses (older browsers / non-secure contexts).
 */
export const copyToClipboard = (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
    } catch {
      /* no-op */
    }
    document.body.removeChild(textArea);
    resolve();
  });
};

/**
 * Short "how long ago" text for timeline entries, so staff can see at a glance
 * that three events happened seconds apart rather than parsing timestamps.
 */
export const formatRelativeTime = (value) => {
  if (!value) return '';
  const diffMs = Date.now() - new Date(value).getTime();
  if (diffMs < 0) return 'just now';
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  return `${Math.floor(months / 12)} year${months < 24 ? '' : 's'} ago`;
};

/**
 * Turn one raw registry event into something a coordinator can read.
 *
 * The timeline used to print the enum name (REMINDER_SENT) above a JSON dump of
 * the row's meta, which answered "what does the database contain" rather than
 * "what happened to this vendor". Each event becomes a sentence, an icon and a
 * colour instead; the raw meta stays available behind a details toggle for
 * debugging.
 */
export const describeEvent = (ev) => {
  const meta = ev?.meta || {};
  const who = ev?.actor_name || ev?.actor_email || null;
  const by = who ? ` by ${who}` : '';

  switch (ev?.event_type) {
    case 'ISSUED':
      return {
        title: 'Link created and emailed',
        detail: `A fresh ${(STAGE_LABELS[meta.stage] || 'onboarding').toLowerCase()} link was generated and sent to the vendor${by}.`,
        icon: 'Send',
        tone: 'blue',
      };
    case 'VIEWED':
      return {
        title:
          ev?.count > 1 ? `Vendor opened the link ${ev.count} times` : 'Vendor opened the link',
        detail:
          ev?.count > 1
            ? `First opened ${formatRelativeTime(ev.firstAt)}, most recently ${formatRelativeTime(ev.lastAt)}. Nothing submitted yet — repeated opens usually mean they started the form but did not finish.`
            : 'The vendor clicked through and the form loaded. No submission yet.',
        icon: 'Eye',
        tone: 'blue',
      };
    case 'CONSUMED':
      return {
        title: 'Vendor completed this step',
        detail: 'The form was submitted, so this link is now used up and cannot be reopened.',
        icon: 'CheckCircle2',
        tone: 'green',
      };
    case 'REMINDER_SENT':
      return {
        title: meta.manual ? 'Reminder sent manually' : 'Automatic reminder sent',
        detail: meta.manual
          ? `A nudge was emailed to ${meta.recipient || 'the vendor'}${by}.`
          : `The system emailed a reminder because the link was nearing expiry${meta.reminder_key === 't24' ? ' (under 24 hours left)' : ' (under 72 hours left)'}.`,
        icon: 'BellRing',
        tone: 'amber',
      };
    case 'REMINDER_SUPPRESSED':
      return {
        title: 'Reminder held back',
        detail:
          meta.reason === 'recipient_rate_limit'
            ? 'This vendor had already been emailed in the last 24 hours, so the reminder was skipped to avoid spamming them.'
            : 'A reminder was due but was not sent.',
        icon: 'BellOff',
        tone: 'slate',
      };
    case 'SUPERSEDED':
      return {
        title: 'Replaced by a newer link',
        detail:
          'Someone regenerated this link. This copy stopped working the moment the new one was created — only the newest link is valid.',
        icon: 'RefreshCw',
        tone: 'slate',
      };
    case 'REVOKED':
      return {
        title: 'Link cancelled',
        detail: meta.reason
          ? `Cancelled${by}. Reason: ${meta.reason}`
          : `The link was cancelled${by} and no longer works.`,
        icon: 'Ban',
        tone: 'red',
      };
    case 'EXPIRED':
      return {
        title: 'Link expired',
        detail: 'The deadline passed before the vendor completed this step.',
        icon: 'Clock',
        tone: 'red',
      };
    case 'EXPIRY_EXTENDED':
      return {
        title: 'Deadline extended',
        detail: `The expiry was pushed to ${meta.new_expires_at ? formatDateTime(meta.new_expires_at) : 'a later date'}${by}.${meta.reason ? ` Reason: ${meta.reason}` : ''}`,
        icon: 'CalendarClock',
        tone: 'blue',
      };
    case 'BACKFILLED':
      return {
        title: 'Imported from historical data',
        detail:
          'This entry was reconstructed when the link registry was introduced, so earlier history is not available.',
        icon: 'Database',
        tone: 'slate',
      };
    default:
      return {
        title: (ev?.event_type || 'Event').replace(/_/g, ' ').toLowerCase(),
        detail: '',
        icon: 'Circle',
        tone: 'slate',
      };
  }
};

/**
 * Collapse consecutive repeats of the same event into one entry.
 *
 * recordViewed() appends a VIEWED row on every single page load, so a vendor who
 * refreshes the form twenty times produces twenty identical timeline entries and
 * buries everything else. The repeats are real audit data and are kept — they
 * are just presented as one line with a count and a first/last range.
 *
 * Returns entries shaped like an event, plus { count, firstAt, lastAt }.
 */
export const groupEvents = (events = []) => {
  const out = [];
  for (const ev of events) {
    const prev = out[out.length - 1];
    if (prev && prev.event_type === ev.event_type && ev.event_type === 'VIEWED') {
      prev.count += 1;
      prev.lastAt = ev.occurred_at;
      prev.occurred_at = ev.occurred_at;
      continue;
    }
    out.push({ ...ev, count: 1, firstAt: ev.occurred_at, lastAt: ev.occurred_at });
  }
  return out;
};

export const EVENT_TONE_CLASS = {
  blue: { dot: 'bg-blue-500', icon: 'text-blue-600', ring: 'bg-blue-50 border-blue-200' },
  green: { dot: 'bg-green-500', icon: 'text-green-600', ring: 'bg-green-50 border-green-200' },
  amber: { dot: 'bg-amber-500', icon: 'text-amber-600', ring: 'bg-amber-50 border-amber-200' },
  red: { dot: 'bg-red-500', icon: 'text-red-600', ring: 'bg-red-50 border-red-200' },
  slate: { dot: 'bg-slate-400', icon: 'text-slate-500', ring: 'bg-slate-50 border-slate-200' },
};
